'use client';

import { c as _c } from "react/compiler-runtime";
import { Icon } from '@repo/ui';
import { ArrowUp } from '@repo/ui/icons';
import { flexRender, type HeaderGroup } from '@tanstack/react-table';
import { column_getCanSort, column_getIsSorted, column_toggleSorting, header_getSize } from '@tanstack/react-table/static-functions';
import { type ComponentPropsWithRef, forwardRef, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '../../../utils/cn';
import { useColumnDrag } from './useColumnDrag';
const Root = forwardRef<HTMLTableElement, ComponentPropsWithRef<'table'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-card w-full border-separate border-spacing-0 tabular-nums", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <table ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
Root.displayName = 'CompactTable';
const HeadRow = forwardRef<HTMLTableRowElement, ComponentPropsWithRef<'tr'>>((t0, ref) => {
  const $ = _c(7);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className || $[4] !== props || $[5] !== ref) {
    t1 = <tr ref={ref} className={className} {...props} />;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
});
HeadRow.displayName = 'CompactTable.HeadRow';
interface HeaderCellProps extends ComponentPropsWithRef<'th'> {
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | false;
  isDragging?: boolean;
  translateX?: number;
  isAnimating?: boolean;
  onSort?: () => void;
  sortLabel?: string;
  onPointerDown?: (e: ReactPointerEvent<HTMLTableCellElement>) => void;
  isDraggable?: boolean;
}
const HeaderCell = forwardRef<HTMLTableCellElement, HeaderCellProps>((t0, ref) => {
  const $ = _c(38);
  let children;
  let className;
  let isAnimating;
  let isDraggable;
  let isDragging;
  let onPointerDownProp;
  let onSort;
  let props;
  let sortLabel;
  let sortable;
  let sorted;
  let style;
  let translateX;
  if ($[0] !== t0) {
    ({
      className,
      sortable,
      sorted,
      isDragging,
      translateX,
      isAnimating,
      onSort,
      sortLabel,
      onPointerDown: onPointerDownProp,
      isDraggable,
      children,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isAnimating;
    $[4] = isDraggable;
    $[5] = isDragging;
    $[6] = onPointerDownProp;
    $[7] = onSort;
    $[8] = props;
    $[9] = sortLabel;
    $[10] = sortable;
    $[11] = sorted;
    $[12] = style;
    $[13] = translateX;
  } else {
    children = $[1];
    className = $[2];
    isAnimating = $[3];
    isDraggable = $[4];
    isDragging = $[5];
    onPointerDownProp = $[6];
    onSort = $[7];
    props = $[8];
    sortLabel = $[9];
    sortable = $[10];
    sorted = $[11];
    style = $[12];
    translateX = $[13];
  }
  const t1 = isDraggable && "cursor-grab select-none";
  const t2 = isDragging && "shadow-sm opacity-80";
  const t3 = isAnimating && "transition-transform duration-200 ease-out";
  let t4;
  if ($[14] !== className || $[15] !== t1 || $[16] !== t2 || $[17] !== t3) {
    t4 = cn("group/header px-md py-xs text-3xs text-secondary-foreground bg-card border-border border-b-[0.5px] z-1 sticky top-0 h-9 text-start font-normal whitespace-nowrap", t1, t2, t3, className);
    $[14] = className;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const t5 = isDragging ? 50 : undefined;
  const t6 = translateX != null && translateX !== 0 ? `translateX(${translateX}px)` : undefined;
  let t7;
  if ($[19] !== style || $[20] !== t5 || $[21] !== t6) {
    t7 = {
      ...style,
      zIndex: t5,
      transform: t6
    };
    $[19] = style;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== onSort || $[24] !== sortLabel || $[25] !== sortable || $[26] !== sorted) {
    t8 = sortable && <button type="button" data-sort-button={true} onClick={onSort} aria-label={sortLabel} className="inline-flex cursor-pointer items-center border-none bg-transparent p-0"><Icon icon={<ArrowUp />} className={cn("text-muted-foreground hover:text-secondary-foreground size-4 transition-all duration-200", !sorted && "scale-75 rotate-180 opacity-0 group-hover/header:scale-100 group-hover/header:opacity-100", sorted === "asc" && "rotate-0", sorted === "desc" && "rotate-180")} /></button>;
    $[23] = onSort;
    $[24] = sortLabel;
    $[25] = sortable;
    $[26] = sorted;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== children || $[29] !== t8) {
    t9 = <span className="gap-2xs inline-flex items-center">{children}{t8}</span>;
    $[28] = children;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== onPointerDownProp || $[32] !== props || $[33] !== ref || $[34] !== t4 || $[35] !== t7 || $[36] !== t9) {
    t10 = <th ref={ref} onPointerDown={onPointerDownProp} className={t4} style={t7} {...props}>{t9}</th>;
    $[31] = onPointerDownProp;
    $[32] = props;
    $[33] = ref;
    $[34] = t4;
    $[35] = t7;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  return t10;
});
HeaderCell.displayName = 'CompactTable.HeaderCell';
interface HeadProps extends ComponentPropsWithRef<'thead'> {
  // Table chrome is row-type-agnostic; HeaderGroup is invariant in TData, so
  // `unknown` breaks assignment from the concrete HeaderGroup at call sites.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerGroups?: HeaderGroup<any, any>[];
  columnLabels?: Record<string, string>;
  onColumnOrderChange?: (newOrder: string[]) => void;
  /** Full column order including hidden columns, used to preserve hidden IDs on reorder */
  columnOrder?: string[];
}
const Head = forwardRef<HTMLTableSectionElement, HeadProps>(({
  headerGroups,
  columnLabels,
  onColumnOrderChange,
  columnOrder,
  children,
  ...props
}, ref) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isDraggable,
    draggedId,
    getTranslateX,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    thRefCallback
  } = useColumnDrag({
    headerGroups,
    onColumnOrderChange,
    columnOrder
  });
  return <thead ref={ref} {...props}>
        {headerGroups ? headerGroups.map(headerGroup => <HeadRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
        const isSortable = column_getCanSort(header.column);
        const sorted = column_getIsSorted(header.column);
        const isThisDragged = draggedId === header.id;
        const tx = getTranslateX(header.id, index);
        const sortLabel = `Sort by ${columnLabels?.[header.id] ?? header.id}`;
        return <HeaderCell key={header.id} ref={el => thRefCallback(header.id, el)} colSpan={header.colSpan} sortable={isSortable} sorted={sorted} isDragging={isThisDragged} translateX={tx} isAnimating={!isThisDragged && draggedId != null} isDraggable={isDraggable} style={{
          width: header_getSize(header)
        }} title={isSortable ? sortLabel : undefined} sortLabel={isSortable ? sortLabel : undefined} onSort={() => {
          if (isSortable) column_toggleSorting(header.column);
        }} onPointerDown={isDraggable ? e => handlePointerDown(e, header.id) : undefined} onPointerMove={isDraggable ? handlePointerMove : undefined} onPointerUp={isDraggable ? handlePointerUp : undefined}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </HeaderCell>;
      })}
              </HeadRow>) : children}
      </thead>;
});
Head.displayName = 'CompactTable.Head';
const Row = forwardRef<HTMLTableRowElement, ComponentPropsWithRef<'tr'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <tr ref={ref} className={t1} data-testid="table-row" {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
Row.displayName = 'CompactTable.Row';
const Cell = forwardRef<HTMLTableCellElement, ComponentPropsWithRef<'td'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <td ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
Cell.displayName = 'CompactTable.Cell';
interface CellContentProps extends ComponentPropsWithRef<'div'> {
  hoverable?: boolean;
}
const CellContent = forwardRef<HTMLDivElement, CellContentProps>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      hoverable: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const hoverable = t1 === undefined ? true : t1;
  const t2 = hoverable && "group-hover:bg-accent";
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("py-xs pl-md border-b-[0.5px] border-border flex h-[63px] items-center pr-[40px] whitespace-nowrap", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== ref || $[9] !== t3) {
    t4 = <div ref={ref} className={t3} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
});
CellContent.displayName = 'CompactTable.CellContent';
interface SkeletonRowProps {
  cells: ReactNode[];
  keyPrefix: string;
}
const SkeletonRow = t0 => {
  const $ = _c(7);
  const {
    cells,
    keyPrefix
  } = t0;
  let t1;
  if ($[0] !== cells || $[1] !== keyPrefix) {
    let t2;
    if ($[3] !== keyPrefix) {
      t2 = (cell, cellIndex) => <Cell key={`${keyPrefix}-${cellIndex}`}><CellContent hoverable={false}>{cell}</CellContent></Cell>;
      $[3] = keyPrefix;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = cells.map(t2);
    $[0] = cells;
    $[1] = keyPrefix;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <tr data-testid="table-skeleton-row">{t1}</tr>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
SkeletonRow.displayName = 'CompactTable.SkeletonRow';
type BodyProps = ComponentPropsWithRef<'tbody'>;
const Body = forwardRef<HTMLTableSectionElement, BodyProps>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("[&>tr:last-child_div]:border-b-0", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <tbody ref={ref} className={t1} {...props}>{children}</tbody>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
Body.displayName = 'CompactTable.Body';
export const CompactTable = Object.assign(Root, {
  Head,
  HeadRow,
  HeaderCell,
  Body,
  Row,
  Cell,
  CellContent,
  SkeletonRow
});